import React, { useState } from 'react';
import { Trash2, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import logger from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DeleteAccountSection = ({ onAccountDeleted }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteForm, setShowDeleteForm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (deleteConfirmation !== 'DELETE MY ACCOUNT') {
      toast.error("Please type 'DELETE MY ACCOUNT' to confirm");
      return;
    }
    if (!deletePassword) {
      toast.error('Please enter your password');
      return;
    }

    setDeleting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/auth/delete-my-account`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: deletePassword, confirmation: deleteConfirmation })
      });

      if (res.ok) {
        toast.success('Account deleted successfully');
        localStorage.clear();
        if (onAccountDeleted) onAccountDeleted();
        else window.location.href = '/';
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete account');
      }
    } catch (err) {
      logger.error(err);
      toast.error('Network error');
    } finally {
      setDeleting(false);
    }
  };

  const resetForm = () => {
    setShowDeleteForm(false);
    setShowDeleteConfirm(false);
    setDeletePassword('');
    setDeleteConfirmation('');
  };

  return (
    <div className="border border-red-200 rounded-xl p-4 bg-red-50/50">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <p className="font-semibold text-red-700 text-sm">Delete My Account</p>
          <p className="text-xs text-slate-600 mt-0.5">
            Permanently delete your account and all associated data. This action cannot be undone.
          </p>
        </div>
        {!showDeleteConfirm && (
          <Button data-testid="delete-account-trigger-btn" onClick={() => setShowDeleteConfirm(true)}
            variant="outline" size="sm" className="shrink-0 border-red-300 text-red-600 hover:bg-red-50">
            <Trash2 className="w-4 h-4 mr-2" /> Delete Account
          </Button>
        )}
      </div>

      {showDeleteConfirm && !showDeleteForm && (
        <div className="mt-4 border-t border-red-200 pt-4">
          <div className="flex items-start gap-2 bg-red-100 rounded-lg p-3">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <p className="text-sm text-red-700 font-medium">Are you sure you want to delete your account? This action is permanent and cannot be undone.</p>
          </div>
          <div className="flex gap-2 mt-3">
            <Button data-testid="delete-account-confirm-sure-btn" onClick={() => setShowDeleteForm(true)}
              size="sm" className="bg-red-600 hover:bg-red-700 text-white">Yes, I'm sure</Button>
            <Button data-testid="delete-account-cancel-sure-btn" onClick={() => setShowDeleteConfirm(false)}
              variant="outline" size="sm">Cancel</Button>
          </div>
        </div>
      )}

      {showDeleteForm && (
        <div className="mt-4 space-y-3 border-t border-red-200 pt-4">
          <div className="flex items-start gap-2 bg-red-100 rounded-lg p-3">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div className="text-xs text-red-700">
              <p className="font-semibold">This will permanently delete:</p>
              <ul className="mt-1 space-y-0.5 list-disc pl-4">
                <li>Your account and profile</li>
                <li>All investments and entities</li>
                <li>Portfolio snapshots and history</li>
                <li>Login history and security settings</li>
              </ul>
            </div>
          </div>

          <div>
            <label className="block text-xs text-slate-600 mb-1">Enter your password</label>
            <input data-testid="delete-account-password" type="password" value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)} placeholder="Your current password"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
          </div>

          <div>
            <label className="block text-xs text-slate-600 mb-1">
              Type <span className="font-mono font-bold text-red-600">DELETE MY ACCOUNT</span> to confirm
            </label>
            <input data-testid="delete-account-confirmation" type="text" value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)} placeholder="DELETE MY ACCOUNT"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono" />
          </div>

          <div className="flex gap-2">
            <Button data-testid="delete-account-confirm-btn" onClick={handleDelete}
              disabled={deleting || deleteConfirmation !== 'DELETE MY ACCOUNT' || !deletePassword}
              size="sm" className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50">
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              {deleting ? 'Deleting...' : 'Permanently Delete'}
            </Button>
            <Button data-testid="delete-account-cancel-btn" onClick={resetForm} variant="outline" size="sm">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeleteAccountSection;
